'use client';

import { Calendar, Clock, MapPin, Church, ExternalLink, Coffee } from 'lucide-react';
import { Memorial } from '@/lib/types';
import { formatDate, formatTime } from '@/lib/utils';

interface ServiceInfoProps {
  memorial: Memorial;
}

export function ServiceInfo({ memorial }: ServiceInfoProps) {
  const hasService = memorial.placeOfService || memorial.dateOfService;
  const hasBurial = memorial.placeOfBurial || memorial.dateOfBurial;

  if (!hasService && !hasBurial) return null;

  const getMapsUrl = (address: string | null) => {
    if (!address) return null;
    return `https://maps.google.com/?q=${encodeURIComponent(address)}`;
  };

  return (
    <section className="memorial-section bg-memorial-surface">
      <div className="max-w-4xl mx-auto px-4">
        <h2 className="font-serif text-2xl md:text-3xl text-memorial-primary text-center mb-10">
          Trauerfeier & Beisetzung
        </h2>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Service info */}
          {hasService && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-memorial-border card-hover">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-memorial-primary/10 flex items-center justify-center flex-shrink-0">
                  <Church className="w-6 h-6 text-memorial-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-serif text-xl text-memorial-primary mb-3">Trauerfeier</h3>

                  {memorial.placeOfService && (
                    <p className="font-medium text-memorial-text-primary mb-1">
                      {memorial.placeOfService}
                    </p>
                  )}

                  {memorial.addressOfService && (
                    <p className="text-sm text-memorial-text-secondary mb-3">
                      {memorial.addressOfService}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-4 text-sm">
                    {memorial.dateOfService && (
                      <div className="flex items-center gap-2 text-memorial-text-secondary">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(memorial.dateOfService)}</span>
                      </div>
                    )}
                    {memorial.timeOfService && (
                      <div className="flex items-center gap-2 text-memorial-text-secondary">
                        <Clock className="w-4 h-4" />
                        <span>{formatTime(memorial.timeOfService)}</span>
                      </div>
                    )}
                  </div>

                  {memorial.addressOfService && getMapsUrl(memorial.addressOfService) && (
                    <a
                      href={getMapsUrl(memorial.addressOfService)!}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-4 text-sm text-memorial-secondary hover:text-memorial-primary transition-colors"
                    >
                      <span>Route planen</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Burial info */}
          {hasBurial && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-memorial-border card-hover">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-memorial-secondary/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-memorial-secondary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-serif text-xl text-memorial-primary mb-3">Beisetzung</h3>

                  {memorial.placeOfBurial && (
                    <p className="font-medium text-memorial-text-primary mb-1">
                      {memorial.placeOfBurial}
                    </p>
                  )}

                  {memorial.addressOfBurial && (
                    <p className="text-sm text-memorial-text-secondary mb-3">
                      {memorial.addressOfBurial}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-4 text-sm">
                    {memorial.dateOfBurial && (
                      <div className="flex items-center gap-2 text-memorial-text-secondary">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(memorial.dateOfBurial)}</span>
                      </div>
                    )}
                    {memorial.timeOfBurial && (
                      <div className="flex items-center gap-2 text-memorial-text-secondary">
                        <Clock className="w-4 h-4" />
                        <span>{formatTime(memorial.timeOfBurial)}</span>
                      </div>
                    )}
                  </div>

                  {memorial.addressOfBurial && getMapsUrl(memorial.addressOfBurial) && (
                    <a
                      href={getMapsUrl(memorial.addressOfBurial)!}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-4 text-sm text-memorial-secondary hover:text-memorial-primary transition-colors"
                    >
                      <span>Route planen</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Occasion Info */}
        {(memorial.occasion1 || memorial.occasion2) && (
          <div className="mt-6 bg-memorial-muted/50 rounded-xl p-5 border border-memorial-border">
            <div className="flex items-start gap-3">
              <Coffee className="w-5 h-5 text-memorial-secondary mt-0.5 flex-shrink-0" />
              <div className="text-memorial-text-secondary">
                {memorial.occasion1 && <p>{memorial.occasion1}</p>}
                {memorial.occasion2 && <p className="mt-1">{memorial.occasion2}</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
